import { useState, useEffect } from "react";
import {
  Phone,
  MapPin,
  Mail,
  UserPlus,
  Briefcase,
  Trash2
} from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api/api";
import Toast, { useToast } from "../components/Toast";
import ConfirmDialog, { useConfirm } from "./ConfirmDialog";

export default function AccountDetails() {
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const { id } = useParams();
  const custId = parseInt(id, 10) || 1;
  const navigate = useNavigate();
  const { toast, showToast, hideToast } = useToast();
  const { confirm, confirmState, handleConfirm, handleCancel } = useConfirm();

  const [accountData, setAccountData] = useState({
    cust_id: "",
    cust_name: "",
    cust_address: "",
    cust_city: "",
    cust_state: "",
    cust_pincode: "",
    cust_cin: "",
    cust_gst: "",
    cust_account_manager_id: "", 
    cust_last_updated_by: "",
    cust_last_updated: "",
    cust_admin_approval: 1,
    account_manager: "",
    last_updated_by_name: ""
  });

  const [contacts, setContacts] = useState([]);
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    designation: "",
  });

  useEffect(() => {
    const fetchCustomerProfile = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/customer/${custId}`);
        if (response.data.success) {
          const fetched = response.data.data;
          setAccountData(fetched);
          setContacts(fetched.contacts || []); 
        }
      } catch (err) {
        console.error("Error reading database record profile:", err);
        showToast(err.response?.data?.message || "Could not retrieve account details profile.", "error");
      } finally {
        setLoading(false);
      }
    };

    if (custId) fetchCustomerProfile();
  }, [custId]);

  const syncProfileWithDatabase = async (currentContactsList) => {
    try {
      const payload = {
        cust_name: accountData.cust_name,
        cust_address: accountData.cust_address,
        cust_city: accountData.cust_city,
        cust_state: accountData.cust_state,
        cust_pincode: accountData.cust_pincode,
        cust_cin: accountData.cust_cin,
        cust_gst: accountData.cust_gst,
        cust_account_manager_id: accountData.cust_account_manager_id,
        cust_admin_approval: Number(accountData.cust_admin_approval),
        contacts: currentContactsList 
      };

      const res = await api.put(`/customer/edit/${custId}`, payload);
      return res.data.success;
    } catch (err) {
      console.error("Database sync error:", err);
      showToast(err.response?.data?.message || "Failed to commit changes to database.", "error");
      return false;
    }
  };

  const saveProfileChanges = async () => {
    const success = await syncProfileWithDatabase(contacts);
    if (success) {
      setIsEditing(false);
      showToast("Customer modifications saved successfully.", "success");
    }
  };

  const handleDeleteAccount = async () => {
    if (!(await confirm("Are you sure you want to permanently delete this account configuration profile?"))) return;
    try {
      const res = await api.delete(`/customer/delete/${custId}`);
      if (res.data.success) {
        showToast("Account deleted successfully.", "success");
        setTimeout(() => navigate('/customers'), 1200);
      }
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to delete account mapping record.", "error");
    }
  };

  const handleAccountChange = (field, value) => {
    setAccountData((prev) => ({ ...prev, [field]: value }));
  };

  const addContact = async (e) => {
    e.preventDefault();
    if (!contactForm.name.trim()) {
      showToast("Name is a required tracking property.", "warning");
      return;
    }
    
    const updatedContacts = [...contacts, contactForm];
    setContacts(updatedContacts);
    setContactForm({ name: "", email: "", phone: "", designation: "" });
    await syncProfileWithDatabase(updatedContacts);
  };

  const removeContact = async (indexToRemove) => {
    const updatedContacts = contacts.filter((_, idx) => idx !== indexToRemove);
    setContacts(updatedContacts);
    const success = await syncProfileWithDatabase(updatedContacts);
    if (success) showToast("Contact removed successfully.", "success");
  };

  if (loading) {
    return (
      <div className="ml-[60px] pt-16 p-3 sm:p-6 flex justify-center items-center min-h-screen text-slate-500 font-bold">
        <Toast toast={toast} onClose={hideToast} />
        <ConfirmDialog confirmState={confirmState} onConfirm={handleConfirm} onCancel={handleCancel} />
        Synchronizing with master system database metrics...
      </div>
    );
  }

  return (
    <div className="ml-[60px] pt-16 p-3 sm:p-6 bg-[#F1F5F9] min-h-screen text-slate-800 antialiased mt-12">
      <Toast toast={toast} onClose={hideToast} />
        <ConfirmDialog confirmState={confirmState} onConfirm={handleConfirm} onCancel={handleCancel} />
      
      {/* HEADER BAR */}
      <div className="bg-white rounded-xl border border-slate-200/80 shadow-xs px-6 py-5 flex justify-between items-center mb-5">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center font-bold text-xl border border-blue-100">
            {accountData.cust_name?.charAt(0).toUpperCase() || "A"}
          </div>
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Account Profile</p>
            <h1 className="text-2xl font-bold text-slate-800 tracking-tight">
              {accountData.cust_name || "Untitled Workspace"}
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            onClick={() => (isEditing ? saveProfileChanges() : setIsEditing(true))}
            className={`px-5 py-2.5 rounded-lg text-sm font-bold border transition shadow-xs ${
              isEditing 
                ? "bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700" 
                : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
            }`}
          >
            {isEditing ? "Save Changes" : "Edit Details"}
          </button>
          {/* <button 
            onClick={handleDeleteAccount}
            className="px-5 py-2.5 border border-red-200 rounded-lg bg-red-50 text-red-600 font-bold text-sm hover:text-white hover:bg-red-600 hover:border-red-600 transition shadow-xs"
          >
            Delete Account
          </button> */}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 max-w-full items-start">
        
        {/* STRUCTURAL METADATA INFO */}
        <div className="space-y-4">
          <Card title="About & Structural Details">
            <EditableRow label="Customer Name" value={accountData.cust_name} isEditing={isEditing} onChange={(v) => handleAccountChange("cust_name", v)} />
            <EditableRow label="CIN Number" value={accountData.cust_cin} isEditing={isEditing} onChange={(v) => handleAccountChange("cust_cin", v)} />
            <EditableRow label="GST Number" value={accountData.cust_gst} isEditing={isEditing} onChange={(v) => handleAccountChange("cust_gst", v)} />
            
            {/* Show Account Manager Name instead of ID */}
            <EditableRow 
              label="Account Manager" 
              value={accountData.account_manager || "Unassigned"} 
              isEditing={false} // Kept false here to prevent changing raw string names directly without dropdown map lists
            />

            {/* Custom handling for Admin Approval Status mapping */}
            <div className="flex justify-between items-center border-b border-slate-100/70 py-3.5 last:border-none gap-4">
              <div className="flex items-center gap-2 text-sm font-bold text-slate-500 whitespace-nowrap">
                Admin Approval Status
              </div>
              <div className="flex-1 flex justify-end">
                {isEditing ? (
                  <select
                    value={accountData.cust_admin_approval}
                    onChange={(e) => handleAccountChange("cust_admin_approval", e.target.value)}
                    className="text-sm border border-slate-200 rounded-md px-2 py-1.5 text-right bg-white w-full max-w-[240px] text-slate-800 font-bold focus:outline-none"
                  >
                    <option value={1}>Approved</option>
                    <option value={0}>Denied</option>
                  </select>
                ) : (
                  <span className={`text-sm font-bold px-2.5 py-1 rounded-full ${
                    Number(accountData.cust_admin_approval) === 1 
                      ? "bg-emerald-50 text-emerald-700" 
                      : "bg-red-50 text-red-700"
                  }`}>
                    {Number(accountData.cust_admin_approval) === 1 ? "Approved" : "Denied"}
                  </span>
                )}
              </div>
            </div>

            <EditableRow label="Address" value={accountData.cust_address} isEditing={isEditing} icon={<MapPin size={15} className="text-slate-400" />} onChange={(v) => handleAccountChange("cust_address", v)} />
            <EditableRow label="City" value={accountData.cust_city} isEditing={isEditing} icon={<MapPin size={15} className="text-slate-400" />} onChange={(v) => handleAccountChange("cust_city", v)} />
            <EditableRow label="State" value={accountData.cust_state} isEditing={isEditing} icon={<MapPin size={15} className="text-slate-400" />} onChange={(v) => handleAccountChange("cust_state", v)} />
            <EditableRow label="Pincode" value={accountData.cust_pincode} isEditing={isEditing} icon={<MapPin size={15} className="text-slate-400" />} onChange={(v) => handleAccountChange("cust_pincode", v)} />
          </Card>

          <Card title="Audit Logs">
            <div className="space-y-4 text-sm text-slate-600">
              <div>
                <p className="font-bold text-slate-400 text-xs uppercase tracking-wider">Last Database Modification By</p>
                <p className="text-base font-bold text-slate-800 mt-1">{accountData.last_updated_by_name || "System"}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* INTERACTIVE POINTS OF CONTACT MANAGEMENT */}
        <div className="space-y-4">
          <Card title={`Points of Contacts (${contacts.length})`}>
            <form onSubmit={addContact} className="bg-slate-50/80 border border-slate-200/60 rounded-xl p-4 mb-4 space-y-3">
              <p className="text-xs font-bold text-slate-700 uppercase tracking-wide flex items-center gap-1.5">
                <UserPlus size={15} className="text-blue-600" /> New Point Person
              </p>
              <div className="grid grid-cols-1 gap-2">
                <input type="text" placeholder="Full Name" value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })} className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800" />
                <input type="text" placeholder="Designation / Role" value={contactForm.designation} onChange={(e) => setContactForm({ ...contactForm, designation: e.target.value })} className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800" />
                <input type="email" placeholder="Email Address" value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800" />
                <input type="text" placeholder="Direct Contact No" value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800" />
              </div>
              <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 rounded-lg text-sm transition">
                Add Contact Record
              </button>
            </form>

            <div className="space-y-2.5 max-h-[600px] overflow-y-auto">
              {contacts.map((contact, idx) => (
                <div key={idx} className="relative group border border-slate-200 bg-white rounded-xl p-4 hover:shadow-xs transition">
                  <button onClick={() => removeContact(idx)} className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition">
                    <Trash2 size={15} />
                  </button>
                  <p className="font-bold text-base text-slate-800">{contact.name || contact.poc_name}</p>
                  <div className="mt-2.5 space-y-1.5 text-sm text-slate-500">
                    <p className="flex items-center gap-2 text-slate-700 font-semibold">
                      <Briefcase size={14} className="text-slate-400" /> {contact.designation || contact.poc_designation || "N/A"}
                    </p>
                    <p className="flex items-center gap-2 font-medium">
                      <Mail size={14} className="text-slate-400" /> {contact.email || contact.poc_email || "N/A"}
                    </p>
                    {(contact.phone || contact.poc_contact) && (
                      <p className="flex items-center gap-2 font-medium">
                        <Phone size={14} className="text-slate-400" /> {contact.phone || contact.poc_contact}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200/80 shadow-xs p-5">
      {title && <h3 className="font-bold text-xs text-slate-400 uppercase tracking-wider mb-4 border-b border-slate-50 pb-2.5">{title}</h3>}
      {children}
    </div>
  );
}

function EditableRow({ label, value, onChange, icon, isEditing }) {
  return (
    <div className="flex justify-between items-center border-b border-slate-100/70 py-3.5 last:border-none gap-4">
      <div className="flex items-center gap-2 text-sm font-bold text-slate-500 whitespace-nowrap">
        {icon} {label}
      </div>
      <div className="flex-1 flex justify-end">
        {isEditing ? (
          <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} className="text-sm border border-slate-200 rounded-md px-3 py-1.5 text-right bg-white w-full max-w-[240px] text-slate-800 font-bold focus:outline-none" />
        ) : (
          <span className="text-sm font-bold text-slate-800 break-all text-right">{value || "—"}</span>
        )}
      </div>
    </div>
  );
}